import { useCallback, useEffect, useId, useRef, useState } from 'react';

interface DraftGuard {
  scope: string;
  dirty: () => boolean;
  saved: () => void;
}
const guards = new Map<string, DraftGuard>();
const currentPath = () => (typeof window === 'undefined' ? '/' : window.location.pathname);

export function hasUnsavedChanges() {
  return [...guards.values()].some((guard) => guard.dirty());
}

/** Call only after the complete form save succeeds, before navigating. */
export function markFormSaved(scope = currentPath()) {
  for (const guard of guards.values()) {
    if (guard.scope === scope) guard.saved();
  }
}

/** Standalone forms can use this without a router; the root owns the prompt. */
export function useUnsavedChanges(draft: unknown, scope = currentPath(), enabled = true) {
  const id = useId();
  const fingerprint = JSON.stringify(draft);
  const current = useRef(fingerprint);
  current.current = fingerprint;
  const initial = useRef(fingerprint);
  const active = useRef(enabled);
  active.current = enabled;
  const [, redraw] = useState(0);
  const markSaved = useCallback((savedDraft?: unknown) => {
    initial.current = savedDraft === undefined ? current.current : JSON.stringify(savedDraft);
    redraw((n) => n + 1);
  }, []);

  useEffect(() => {
    guards.set(id, {
      scope,
      dirty: () => active.current && current.current !== initial.current,
      saved: markSaved,
    });
    return () => {
      guards.delete(id);
    };
  }, [id, scope, markSaved]);

  return { isDirty: enabled && fingerprint !== initial.current, markSaved };
}
